import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MENUS, locate, MenuItem, SubMenu } from './menus';
import { usePrismReadLocal } from '../../utils/prismReadLocal';
import store, {
    actions,
    useSiteRole,
    useMaterialCtx,
    useAuthz,
    useCan,
} from '../../store';
import { allows, allowsAny, Resource, Verb } from '../../api/authz';

// 暗色侧边栏, 对齐原型 <aside class="bg-slate-900">.
// 2026-08-28 改版: 子页从顶部 tab bar (原 Subtabs) 收进侧边栏二级菜单, 顶部只留面包屑.
// 用 locate() 判断当前路径属于哪个菜单/子页: 高亮整个菜单 + 展开它的二级列表.
// 菜单按 RBAC 角色权限过滤 (后端 authz 中间件才是真闸门, 这里只是不给入口).
// replicaOnly 菜单 (数据同步) 只在国服镜像站显示, 美服 (master) 隐藏.
// nonAdminOnly 菜单 (我的工单) 对 admin 隐藏 — admin 走「审批中心」全量视图.
const Sidebar = () => {
    const { pathname } = useLocation();
    const loc = locate(pathname);
    const activeKey = loc?.menu.key;
    const activeSub = loc ? loc.menu.subs[loc.subIdx] : undefined;
    const siteRole = useSiteRole();
    const authz = useAuthz();
    const matCtx = useMaterialCtx();

    // material list 启动后拉一次, 供"材料详情"子菜单解析默认跳转目标.
    // 2026-09-13: 只有能看材料的人才拉 —— 设备/参数管理员没有 material get, 这一发会把材料清单
    // 拉到他们浏览器里 (后端未开 RBAC 时不拦), 而他们的侧栏根本没有"材料详情"这个子菜单.
    const canSeeMaterialList = useCan('material', 'get');
    useEffect(() => {
        if (canSeeMaterialList && !matCtx.loaded) {
            (store.dispatch as any)(actions.material.load());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSeeMaterialList]);

    // 展开哪个菜单: 默认跟随当前路由 (换页自动展开), 用户点箭头可临时展开别的菜单预览
    const [openKey, setOpenKey] = useState<string | undefined>(activeKey);
    useEffect(() => {
        if (activeKey) setOpenKey(activeKey);
    }, [activeKey]);

    // 菜单可见性 (2026-09-01 起只按 RBAC 角色权限, 不再看 isAdmin):
    // 每个菜单/子菜单声明自己的 (resource, verb), 没声明的对所有已准入用户可见.
    // authz 未加载完时不过滤, 避免首屏菜单闪一下再补齐.
    const canSee = (resource?: Resource, verb: Verb = 'get') =>
        !resource || !authz.loaded || allows(authz.rules, resource, verb);
    // 菜单项可声明 anyOf: 满足任一条即可见 (一个页面承载多个资源域时用)
    const canSeeItem = (m: {
        resource?: Resource;
        verb?: Verb;
        anyOf?: { resource: Resource; verb?: Verb }[];
    }) =>
        m.anyOf
            ? !authz.loaded ||
              m.anyOf.some((x) =>
                  allows(authz.rules, x.resource, x.verb ?? 'get'),
              )
            : canSee(m.resource, m.verb);
    // "我的工单"入口: 对任一资源有审批权的人走「审批中心」全量视图, 不需要它.
    const canManageApprovals =
        !authz.loaded || allowsAny(authz.rules, 'approve');
    const visibleMenus = MENUS.filter(
        (m) =>
            (!m.nonAdminOnly || !canManageApprovals) &&
            (!m.replicaOnly || siteRole === 'replica') &&
            canSeeItem(m) &&
            // 子页全部看不到的分组菜单 (如只剩管理页的「基础数据」) 整个不出现
            m.subs.some((s) => canSeeItem(s)),
    );
    const prismLocal = usePrismReadLocal(canSee('data_asset'));
    const visibleSubs = (m: MenuItem) =>
        m.subs.filter((s) => canSeeItem(s) && !(s.hideWhenPrismLocal && prismLocal));
    // 菜单默认跳第一个"当前用户可见"的 sub (如非 admin 点「管理员管理」应落到字典维护而非管理员名单)
    const menuPath = (m: MenuItem): string => {
        const first = visibleSubs(m)[0];
        return first ? resolveSubPath(first) : m.path;
    };
    // 动态路径解析: sub.dynamicPath 被设置时从 redux store 取真实跳转目标
    // (如 '材料详情' → /materials/{curMaterialId}, 默认是列表第一项)
    const resolveSubPath = (sub: SubMenu): string => {
        if (
            sub.dynamicPath === 'materialDetailDefault' &&
            matCtx.curMaterialId
        ) {
            // 若用户已经在某个材料详情页, 用当前 id 保持上下文
            return `/materials/${encodeURIComponent(matCtx.curMaterialId)}`;
        }
        // store 还没加载完时退化到原 path (即 /materials, 走列表)
        return sub.path;
    };

    return (
        <aside className="w-60 shrink-0 bg-slate-900 text-slate-200 flex flex-col">
            <div className="h-14 px-4 flex items-center gap-2 border-b border-slate-800">
                <div className="w-7 h-7 rounded bg-brand-500 grid place-items-center text-white text-sm font-bold">
                    工
                </div>
                <div className="leading-tight">
                    <div className="text-sm font-semibold text-white">
                        工艺参数资产平台
                    </div>
                    <div className="text-[11px] text-slate-400">v1.0</div>
                </div>
            </div>
            <nav className="flex-1 overflow-auto py-3 text-[13px]">
                {visibleMenus.map((m) => {
                    const subs = visibleSubs(m);
                    // 单子页菜单 (工具平台/API Token/我的工单) 不展开二级, 免得和菜单名重复;
                    // alwaysExpandable 的分组菜单 (数据资产) 例外, 单子页也出二级.
                    const expandable =
                        subs.length > 1 || m.alwaysExpandable === true;
                    const open = expandable && openKey === m.key;
                    return (
                        <div key={m.key}>
                            <Link
                                to={menuPath(m)}
                                className={`navitem flex items-center gap-2.5 px-4 py-2.5 cursor-pointer ${
                                    m.key === activeKey
                                        ? 'active'
                                        : 'text-slate-300 hover:bg-slate-800/60'
                                }`}
                            >
                                <span className="w-4 text-center">
                                    {m.icon}
                                </span>
                                <span>{m.label}</span>
                                {expandable && (
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        aria-label={open ? '收起' : '展开'}
                                        onClick={(e) => {
                                            // 只切展开态, 不跳转
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setOpenKey(
                                                open ? undefined : m.key,
                                            );
                                        }}
                                        className={`ml-auto text-[10px] text-slate-500 hover:text-slate-200 transition-transform ${
                                            open ? 'rotate-90' : ''
                                        }`}
                                    >
                                        ▶
                                    </span>
                                )}
                            </Link>
                            {open && (
                                <div className="pb-1">
                                    {subs.map((s) => {
                                        const active = s === activeSub;
                                        // "详情"类子页在没有具体目标时禁用 (灰色不可点)
                                        const enabled = s.enabled
                                            ? s.enabled(pathname)
                                            : true;
                                        if (!enabled && !active) {
                                            return (
                                                <span
                                                    key={s.label}
                                                    title={
                                                        s.disabledHint ||
                                                        '当前不可用'
                                                    }
                                                    className={`flex items-center gap-2 pr-4 py-1.5 text-slate-600 cursor-not-allowed ${s.icon ? 'pl-8' : 'pl-11'}`}
                                                >
                                                    {s.icon && (
                                                        <span className="w-4 text-center text-[13px] leading-none opacity-60">
                                                            {s.icon}
                                                        </span>
                                                    )}
                                                    {s.label}
                                                </span>
                                            );
                                        }
                                        return (
                                            <Link
                                                key={s.label}
                                                to={resolveSubPath(s)}
                                                className={`flex items-center gap-2 pr-4 py-1.5 border-l-[3px] ${s.icon ? 'pl-8' : 'pl-11'} ${
                                                    active
                                                        ? 'border-brand-500 bg-slate-800 text-white font-medium'
                                                        : 'border-transparent text-slate-400 hover:text-slate-100 hover:bg-slate-800/40'
                                                }`}
                                            >
                                                {/* 图标只是区分标识, 不承载语义: 未配图标的子菜单保持原缩进 */}
                                                {s.icon && (
                                                    <span className="w-4 text-center text-[13px] leading-none">
                                                        {s.icon}
                                                    </span>
                                                )}
                                                {s.label}
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>
            <div className="px-4 py-3 border-t border-slate-800 text-[11px] text-slate-400">
                参数管理 + 物理推荐引擎 · 双目标
            </div>
        </aside>
    );
};

export default Sidebar;
